import axios from 'axios'
import { getStaffAccessToken, setStaffAccessToken, clearStaffAccessToken } from './backoffice-auth'

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000'

export const backofficeApi = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
})

backofficeApi.interceptors.request.use((config) => {
  const token = getStaffAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

type QueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void }
let isRefreshing = false
let pendingQueue: QueueEntry[] = []

function drainQueue(error: unknown, token: string | null) {
  for (const { resolve, reject } of pendingQueue) {
    if (error || token === null) reject(error)
    else resolve(token)
  }
  pendingQueue = []
}

backofficeApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status as number | undefined
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const original = error?.config

    if (status === 401 && original && !original._retry) {
      // Não tentar refresh em chamadas dos próprios endpoints de auth
      if (original.url?.includes('/backoffice/auth/')) {
        throw error
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          pendingQueue.push({ resolve, reject })
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`
          return backofficeApi(original)
        })
      }

      original._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post<{ accessToken: string }>(
          `${BASE_URL}/backoffice/auth/refresh`,
          {},
          { withCredentials: true },
        )
        setStaffAccessToken(data.accessToken)
        drainQueue(null, data.accessToken)
        original.headers.Authorization = `Bearer ${data.accessToken}`
        return backofficeApi(original)
      } catch (refreshError) {
        drainQueue(refreshError, null)
        clearStaffAccessToken()
        globalThis.location.href = '/backoffice/login'
        throw refreshError
      } finally {
        isRefreshing = false
      }
    }

    if (status === 403) {
      globalThis.location.href = '/backoffice/403'
    }

    throw error
  },
)
